import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Search, Plus, X, Database, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { getTableTypeIcon } from '../../utils/tableUtils';
import { formatRelativeTime } from '../../utils/dateUtils';

interface Dataset {
  id: string;
  name: string;
  updated_at: string;
  table_type?: string;
  lead_count?: number;
}

interface DatasetGroups {
  today: Dataset[];
  yesterday: Dataset[];
  thisWeek: Dataset[];
  older: Dataset[];
}

interface DatasetPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  datasets: Dataset[];
  onNewDataset: () => void;
  title: string;
  description: string;
  actionLabel: string;
  actionDescription: string;
  onDatasetSelect?: (dataset: Dataset) => void;
  navigateOnSelect?: string;
  isLoading?: boolean;
  loadingDatasetId?: string | null;
}

const groupDatasetsByTime = (datasets: Dataset[]): DatasetGroups => {
  const now = new Date();
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterdayStart = new Date(todayStart.getTime() - 24 * 60 * 60 * 1000);
  const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);

  const groups: DatasetGroups = {
    today: [],
    yesterday: [],
    thisWeek: [],
    older: [],
  };

  datasets.forEach(dataset => {
    const date = new Date(dataset.updated_at);
    if (date >= todayStart) groups.today.push(dataset);
    else if (date >= yesterdayStart) groups.yesterday.push(dataset);
    else if (date >= sevenDaysAgo) groups.thisWeek.push(dataset);
    else groups.older.push(dataset);
  });

  return groups;
};

export const DatasetPickerModal: React.FC<DatasetPickerModalProps> = ({
  isOpen,
  onClose,
  datasets,
  onNewDataset,
  title,
  description,
  actionLabel,
  actionDescription,
  onDatasetSelect,
  navigateOnSelect,
  isLoading = false,
  loadingDatasetId = null,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  const filteredDatasets = (datasets || []).filter(d =>
    d.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const groupedDatasets = groupDatasetsByTime(filteredDatasets);

  const allItems = useMemo(() => [
    { type: 'action' },
    ...(groupedDatasets.today || []).map(d => ({ type: 'dataset' as const, data: d })),
    ...(groupedDatasets.yesterday || []).map(d => ({ type: 'dataset' as const, data: d })),
    ...(groupedDatasets.thisWeek || []).map(d => ({ type: 'dataset' as const, data: d })),
    ...(groupedDatasets.older || []).map(d => ({ type: 'dataset' as const, data: d })),
  ], [groupedDatasets]);

  useEffect(() => {
    if (isOpen) {
      setSearchQuery('');
      setSelectedIndex(0);
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const handleDatasetClick = useCallback((dataset: Dataset) => {
    if (isLoading) return;
    if (onDatasetSelect) {
      onDatasetSelect(dataset);
      // Don't close — let the caller close when ready
      return;
    }
    // Default behavior: navigate to the dataset
    if (navigateOnSelect) {
      navigate(`/datasets/${dataset.id}${navigateOnSelect}`);
    } else {
      navigate(`/datasets/${dataset.id}`);
    }
    onClose();
  }, [navigate, navigateOnSelect, onClose, onDatasetSelect, isLoading]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (isLoading) {
        e.preventDefault();
        return;
      }
      switch (e.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => Math.min(prev + 1, allItems.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => Math.max(prev - 1, 0));
          break;
        case 'Enter': {
          e.preventDefault();
          const selected = allItems[selectedIndex];
          if (selected?.type === 'action') {
            onNewDataset();
            onClose();
          } else if (selected?.type === 'dataset' && 'data' in selected && selected.data) {
            handleDatasetClick(selected.data);
          }
          break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, allItems, onClose, onNewDataset, handleDatasetClick]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [searchQuery]);

  const getItemIndex = (groupName: string, indexInGroup: number): number => {
    let index = 1;
    if (groupName === 'today') return index + indexInGroup;
    index += groupedDatasets.today.length;
    if (groupName === 'yesterday') return index + indexInGroup;
    index += groupedDatasets.yesterday.length;
    if (groupName === 'thisWeek') return index + indexInGroup;
    index += groupedDatasets.thisWeek.length;
    return index + indexInGroup;
  };

  const renderDatasetGroup = (groupTitle: string, items: Dataset[], groupKey: string, isFirst: boolean = false) => {
    if (items.length === 0) return null;

    return (
      <div className="mb-1" key={groupKey}>
        {!isFirst ? (
          <div className="flex items-center gap-3 px-5 pt-4 pb-2">
            <div className="h-px flex-1 bg-neutral-100" />
            <span className="text-[11px] text-neutral-400 font-medium tracking-wide">{groupTitle}</span>
            <div className="h-px flex-1 bg-neutral-100" />
          </div>
        ) : (
          <div className="px-5 pt-1 pb-2">
            <span className="text-[11px] text-neutral-400 font-medium tracking-wide">{groupTitle}</span>
          </div>
        )}
        <div className="space-y-0.5">
          {items.map((dataset, idx) => {
            const itemIndex = getItemIndex(groupKey, idx);
            const isSelected = selectedIndex === itemIndex;
            const Icon = getTableTypeIcon(dataset.table_type);
            const isThisLoading = loadingDatasetId === dataset.id;
            const isDisabled = isLoading && !isThisLoading;

            return (
              <button
                key={dataset.id}
                onClick={() => handleDatasetClick(dataset)}
                onMouseEnter={() => !isLoading && setSelectedIndex(itemIndex)}
                disabled={isLoading}
                className={`w-full flex items-center gap-4 px-5 py-3 text-left transition-all duration-150 ${
                  isThisLoading
                    ? 'bg-neutral-50'
                    : isDisabled
                      ? 'opacity-50 cursor-not-allowed'
                      : isSelected ? 'bg-neutral-50' : 'hover:bg-neutral-50/50'
                }`}
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors ${
                  isSelected || isThisLoading ? 'bg-neutral-200' : 'bg-neutral-100'
                }`}>
                  {isThisLoading ? (
                    <Loader2 className="w-4 h-4 text-neutral-500 animate-spin" />
                  ) : (
                    <Icon className="w-4 h-4 text-neutral-500" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <span className="block text-[14px] text-neutral-800 truncate font-medium leading-tight">
                    {isThisLoading ? 'Creating campaign...' : (dataset.name || 'Untitled Dataset')}
                  </span>
                  <div className="flex items-center gap-2 text-[12px] text-neutral-400">
                    {isThisLoading ? (
                      <span>Setting up recipients</span>
                    ) : (
                      <>
                        <span>{formatRelativeTime(dataset.updated_at)}</span>
                        {dataset.lead_count != null && (
                          <>
                            <span>·</span>
                            <span>{dataset.lead_count.toLocaleString()} results</span>
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-[200]" onClick={isLoading ? undefined : onClose} />
      <div className="fixed inset-0 z-[201] flex items-start justify-center pt-[10vh]" onClick={isLoading ? undefined : onClose}>
        <div
          className="bg-white rounded-2xl shadow-2xl w-full max-w-[640px] overflow-hidden border border-neutral-100 animate-fadeInScale"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-5 pt-5 pb-4 border-b border-neutral-100">
            <h2 className="text-[16px] font-semibold text-neutral-900 mb-1">{title}</h2>
            <p className="text-[13px] text-neutral-500">{description}</p>
          </div>

          <div className="px-5 py-3 bg-neutral-50/50">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
              <input
                ref={inputRef}
                type="text"
                placeholder="Search datasets..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-10 py-2.5 text-[15px] bg-white border border-neutral-200 rounded-xl outline-none placeholder:text-neutral-400 focus:border-neutral-300 focus:ring-2 focus:ring-neutral-100 transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-neutral-400 hover:text-neutral-600 rounded-full hover:bg-neutral-100"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          <div className="max-h-[440px] overflow-y-auto">
            <div className="px-4 pt-3 pb-2">
              <button
                onClick={() => {
                  if (isLoading) return;
                  onNewDataset();
                  onClose();
                }}
                onMouseEnter={() => !isLoading && setSelectedIndex(0)}
                disabled={isLoading}
                className={`w-full flex items-center gap-4 px-4 py-3.5 text-left transition-all duration-150 rounded-xl ${
                  isLoading
                    ? 'opacity-50 cursor-not-allowed bg-neutral-100 text-neutral-700'
                    : selectedIndex === 0 ? 'bg-neutral-900 text-white' : 'bg-neutral-100 hover:bg-neutral-200 text-neutral-700'
                }`}
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${
                  selectedIndex === 0 ? 'bg-white/20' : 'bg-white'
                }`}>
                  <Plus className={`w-5 h-5 ${selectedIndex === 0 ? 'text-white' : 'text-neutral-600'}`} />
                </div>
                <div>
                  <span className="block text-[14px] font-semibold">{actionLabel}</span>
                  <span className={`text-[12px] ${selectedIndex === 0 ? 'text-neutral-300' : 'text-neutral-500'}`}>
                    {actionDescription}
                  </span>
                </div>
              </button>
            </div>

            <div className="pb-3">
              {filteredDatasets.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-center">
                  <div className="w-14 h-14 rounded-2xl bg-neutral-100 flex items-center justify-center mb-4">
                    <Database className="w-7 h-7 text-neutral-300" />
                  </div>
                  <p className="text-[14px] text-neutral-500 font-medium">
                    {searchQuery ? 'No matching datasets' : 'No datasets yet'}
                  </p>
                  <p className="text-[13px] text-neutral-400 mt-1">
                    {searchQuery ? 'Try a different search term' : 'Create a dataset first'}
                  </p>
                </div>
              ) : (
                <>
                  {renderDatasetGroup('Today', groupedDatasets.today, 'today', true)}
                  {renderDatasetGroup('Yesterday', groupedDatasets.yesterday, 'yesterday', groupedDatasets.today.length === 0)}
                  {renderDatasetGroup('This Week', groupedDatasets.thisWeek, 'thisWeek', groupedDatasets.today.length === 0 && groupedDatasets.yesterday.length === 0)}
                  {renderDatasetGroup('Older', groupedDatasets.older, 'older', groupedDatasets.today.length === 0 && groupedDatasets.yesterday.length === 0 && groupedDatasets.thisWeek.length === 0)}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
